import React, { useState } from "react";
import PropTypes from "prop-types";
import { ArrowRight, CalendarX } from "lucide-react";
import { Collapse } from "@mui/material";

import { UIButton, UICard } from "../../ui";
import DashboardEmptyState from "../DashboardEmptyState";
import OngoingEventCard from "../OngoingEventCard";
import useCardGridColumns from "../useCardGridColumns";
import "../dashboardSection.scss";
import "./OngoingEventsData.scss";

// Column count of a card grid (see `--ongoing-columns` in the SCSS).
const columnsStyle = (count) => ({ "--ongoing-columns": count });

/**
 * Dashboard "Ongoing Events data" section — one card per ongoing event with
 * attendance metrics. The first row (4 / 3 / 2 / 1 cards by viewport) shows;
 * the rest reveal on "Show more" in the same column sizes.
 */
const OngoingEventsData = ({
  title,
  viewEventsLabel,
  events,
  metricLabels,
  newApplicantsLabel,
  paymentPeriodLabels,
  nationalityCopy,
  footerCopy,
  showMoreLabel,
  showLessLabel,
  emptyTitle,
  emptyDescription,
  createEventLabel,
  onViewEvents,
  onCreateEvent,
  onEventAction,
}) => {
  const [expanded, setExpanded] = useState(false);
  const columns = useCardGridColumns();
  const hasMore = events.length > columns;
  // Both grids share one column count, so revealed cards keep the row's size.
  const gridStyle = columnsStyle(Math.min(events.length, columns));

  const viewEventsAction = (
    <UIButton
      variant="text"
      className="dashboard-link"
      endIcon={<ArrowRight size={16} strokeWidth={2} />}
      onClick={onViewEvents}
    >
      {viewEventsLabel}
    </UIButton>
  );

  const renderCard = (event) => (
    <OngoingEventCard
      key={event.id}
      name={event.name}
      paymentPeriod={event.paymentPeriod}
      paymentPeriodLabel={paymentPeriodLabels[event.paymentPeriod]}
      metrics={event.metrics}
      metricLabels={metricLabels}
      applicants={event.applicants}
      newApplicantsPercent={event.newApplicantsPercent}
      newApplicantsLabel={newApplicantsLabel}
      nationality={event.nationality}
      nationalityCopy={nationalityCopy}
      dateRange={event.dateRange}
      openReportsCount={event.openReportsCount}
      footerCopy={footerCopy}
      onViewTimesheet={() => onEventAction(event.id, "timesheet")}
      onViewReports={() => onEventAction(event.id, "reports")}
    />
  );

  return (
    <UICard
      title={title}
      action={viewEventsAction}
      className="dashboard-card ongoing-events"
    >
      <div className="ongoing-events__body">
        {events.length === 0 ? (
          <DashboardEmptyState
            icon={<CalendarX />}
            title={emptyTitle}
            description={emptyDescription}
            actionLabel={createEventLabel}
            onAction={onCreateEvent}
          />
        ) : (
          <>
            <div className="ongoing-events__grid" style={gridStyle}>
              {events.slice(0, columns).map(renderCard)}
            </div>

            {hasMore && (
              <>
                <Collapse in={expanded} timeout={320} unmountOnExit>
                  <div
                    className="ongoing-events__grid ongoing-events__grid--rest"
                    style={gridStyle}
                  >
                    {events.slice(columns).map(renderCard)}
                  </div>
                </Collapse>
                <button
                  type="button"
                  className="ongoing-events__toggle"
                  onClick={() => setExpanded((value) => !value)}
                >
                  {expanded ? showLessLabel : showMoreLabel}
                </button>
              </>
            )}
          </>
        )}
      </div>
    </UICard>
  );
};

const eventShape = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  name: PropTypes.string.isRequired,
  paymentPeriod: PropTypes.oneOf(["daily", "monthly"]).isRequired,
  metrics: PropTypes.object.isRequired,
  applicants: PropTypes.number,
  newApplicantsPercent: PropTypes.number,
  nationality: PropTypes.object.isRequired,
  dateRange: PropTypes.string.isRequired,
  openReportsCount: PropTypes.number,
});

OngoingEventsData.propTypes = {
  title: PropTypes.string.isRequired,
  viewEventsLabel: PropTypes.string.isRequired,
  events: PropTypes.arrayOf(eventShape),
  metricLabels: PropTypes.objectOf(PropTypes.string).isRequired,
  newApplicantsLabel: PropTypes.string.isRequired,
  paymentPeriodLabels: PropTypes.objectOf(PropTypes.string).isRequired,
  nationalityCopy: PropTypes.object.isRequired,
  footerCopy: PropTypes.object.isRequired,
  showMoreLabel: PropTypes.string.isRequired,
  showLessLabel: PropTypes.string.isRequired,
  emptyTitle: PropTypes.string.isRequired,
  emptyDescription: PropTypes.string.isRequired,
  createEventLabel: PropTypes.string.isRequired,
  onViewEvents: PropTypes.func.isRequired,
  onCreateEvent: PropTypes.func.isRequired,
  onEventAction: PropTypes.func.isRequired,
};

OngoingEventsData.defaultProps = {
  events: [],
};

export default OngoingEventsData;
